import {
  get,
  identity,
  isEmpty,
  isEqual,
  mapValues,
  omit,
  partial,
  pick,
  unary,
} from 'lodash-es';
import bluebird from 'bluebird';
import qs from 'qs';
import {
  DEFAULT_SPOT,
  DEFAULT_SPOT_SLUGIFIED,
  EMPTY_STRING,
  PATH_DELIMITER,
  QUERY_DELIMITER,
} from '@is24at/react-constants';
import {
  byQuery,
  checkURLChanges,
  isNilOrBlank,
  pickTruthy,
  slugifyString,
  isNotBlank,
} from '@is24at/react-utils';
import { HistoryActionEnum } from '@is24at/react-enums';
import {
  DEFAULT_PLZ_PREFIX,
  FALLBACK_HIERARCHY_ENTITY_NAMES,
  IMMO_MAKLER_SLUG,
  PARAMS_DISPLAY_ORDER,
  PRETTY_URL_ENCODED_PARAMS,
  SearchParamsEnum,
} from 'utils/enums';
import { isEqualOnPaths } from 'utils/utils';

/**
 * Designed to annotate the location state in order to know
 * if the filters can be collapsed / scroll to the top can be done.
 */
export const TRIGGERED_BY_SEARCH_BUTTON = 'triggeredBySearchButton';

/**
 * Designed to annotate the location state with the previous decoded query.
 */
export const PREVIOUS_DECODED_QUERY = 'previousDecodedQuery';

/**
 * The default label geo entry (currently Austria)
 */
export const DEFAULT_AUSTRIA_GEO_ENTRY = {
  label: DEFAULT_SPOT,
};

class PrettyUrlsResultList {
  constructor(localGeoApiClient: any, { sanitize }: any, { mountPath }: any) {
    this.localGeoApiClient = localGeoApiClient;
    this.sanitize = sanitize;
    this.mountPath = mountPath;
  }

  /**
   * Query stringifier function based on a given order array.
   */
  static orderQueryString = (query: any, orderAsArray: any) =>
    qs.stringify(query, {
      sort: (a: any, b: any) => {
        const indexA = orderAsArray.indexOf(a);
        const indexB = orderAsArray.indexOf(b);
        if (indexA === indexB) {
          return 0;
        }
        return indexA > indexB ? 1 : -1;
      },
    });

  /**
   * Strips any unwanted query parameters from the given input.
   */
  static noAppendedQuery = (input: any) =>
    input && input.split(QUERY_DELIMITER)[0];

  localGeoApiClient: any;

  mountPath: any;

  sanitize: any;

  /**
   * Builds the result list location object along with the allowed
   * queries based on the given query and given entity names.
   *
   * This method does not fire any API call and does
   * not return a promise.
   */
  buildLocationWith = (
    query,
    entityNames = FALLBACK_HIERARCHY_ENTITY_NAMES,
  ) => ({
    pathname: this.toPathname(entityNames),

    search: `?${qs.stringify(
      pickTruthy(omit(query, PRETTY_URL_ENCODED_PARAMS)),
    )}`,
  });

  /**
   * Builds a slugified path name from the given entityNames.
   */
  toPathname = (entityNames: any) =>
    `${this.mountPath}${PATH_DELIMITER}${[IMMO_MAKLER_SLUG, ...entityNames]
      .map(unary(slugifyString))
      .join(PATH_DELIMITER)}`;

  /**
   * Builds the result list location object along with
   * the allowed queries based on the given query.
   *
   * Returns a promise with the expected location object.
   */
  buildLocation = (query: any) =>
    bluebird
      .try(() => this.getHierarchyEntityNames(query))
      .then((entityNames: any) => this.buildLocationWith(query, entityNames));

  /**
   * Builds the result list URL along with the allowed queries
   * based on the given query.
   *
   * Returns a promise with the expected pretty URL.
   */
  buildUrl = (query: any) =>
    this.buildLocation(query).then(this.locationToPrettyUrlWithQs);

  /**
   * Converts a location to a pretty URL which contains also the query strings.
   *
   * Returns a URL as a string.
   */
  locationToPrettyUrlWithQs = ({ pathname, search }: any) => {
    const query = qs.parse(search, {
      ignoreQueryPrefix: true,
    });
    return `${pathname}${
      isEmpty(query)
        ? EMPTY_STRING
        : `${QUERY_DELIMITER}${PrettyUrlsResultList.orderQueryString(
            query,
            PARAMS_DISPLAY_ORDER,
          )}`
    }`;
  };

  /**
   * Computes the new location object based on the existing `filter` values and
   * `location` query.
   *
   * The location object contain custom state values.
   * - `PREVIOUS_DECODED_QUERY` is used to keep track of the previous decoded
   * query values.
   * - `TRIGGERED_BY_SEARCH_BUTTON` is used to keep track of the source of the
   * location object (in this case was triggered by search button).
   *
   * Returns a promise containing the location object.
   */
  buildLocationFromFilters = (filterValues: any, location: any) => {
    const truthyFilter = pickTruthy(filterValues);
    const locationQuery = qs.parse(location?.search, {
      ignoreQueryPrefix: true,
    });
    const relevantLocationQueryParams = pick(locationQuery, [
      SearchParamsEnum.spot,
    ]);
    return this.buildLocation({
      ...truthyFilter,
      ...relevantLocationQueryParams,
    }).then((builtLocation: any) => ({
      ...location,
      ...builtLocation,

      state: {
        [TRIGGERED_BY_SEARCH_BUTTON]: true,
        [PREVIOUS_DECODED_QUERY]: pick(truthyFilter, PRETTY_URL_ENCODED_PARAMS),
      },
    }));
  };

  /**
   * Decodes a result list pretty URL to an expected `location`
   * object which contains a `pathname` and a `query`.
   */
  analyzeUrlGet = ({ params, location }: any) => {
    const { pathname } = location;
    const query = byQuery(location);
    const sanitizedLocationQuery = omit(
      this.sanitize(query),
      PRETTY_URL_ENCODED_PARAMS,
    );
    return bluebird
      .try(() => this.decodeUrl({ params, pathname }))
      .then(partial(this.overrideDecodedSpotWithGACLabelIfApplies, location))
      .then((decodedQuery: any) =>
        bluebird.props({
          decodedQuery,
          entityNames: this.getHierarchyEntityNames({
            ...sanitizedLocationQuery,
            ...decodedQuery,
          }),
        }),
      )
      .then(({ decodedQuery, entityNames }: any) => ({
        decodedQuery,
        entityNames,
        builtLocation: this.buildLocationWith(
          { ...sanitizedLocationQuery, ...decodedQuery },
          entityNames,
        ),
      }))
      .then(({ builtLocation, ...rest }: any) => {
        const { hasPathnameChanged, hasQueryChanged } = checkURLChanges(
          builtLocation,
          location,
        );
        return {
          ...rest,
          builtLocation,
          shouldRedirectToPrettyURL: !hasPathnameChanged && hasQueryChanged,
          shouldRedirectToBaseURL: hasPathnameChanged,
        };
      });
  };

  /**
   * Decodes a result list pretty URL to a query.
   *
   * Delivers an object which contains the decoded `region/zipCode` and `spot`.
   */
  decodeUrl = (args: any) =>
    bluebird.try(() => {
      const { regionSlug, districtSlug, citySlug } =
        this.parseUrlFragment(args);
      const resolvedPath = [regionSlug, districtSlug, citySlug]
        .filter(identity)
        .join(PATH_DELIMITER);

      return this.lookupGeoEntityByPath(resolvedPath)
        .then(({ regionId, label, zipCode }: any) => ({
          region: regionId,
          zipCode,
          spot: label,
        }))
        .then(pickTruthy);
    });

  /**
   * Parses the URL fragments and returns the route parameters.
   */
  parseUrlFragment = ({ params }: any) =>
    pickTruthy(mapValues(params, PrettyUrlsResultList.noAppendedQuery));

  /**
   * Looks up for a geo entity based on a URL path.
   *
   * If the path is the default Austria one or
   * nothing is found, the default Austria geo
   * entry is returned.
   */
  lookupGeoEntityByPath = (path: any) => {
    if (isEqual(DEFAULT_SPOT_SLUGIFIED, path) || isNilOrBlank(path)) {
      return bluebird.resolve(DEFAULT_AUSTRIA_GEO_ENTRY);
    }
    return bluebird
      .try(() => this.localGeoApiClient.decodeGeoEntityFrom(path))
      .catch(() => DEFAULT_AUSTRIA_GEO_ENTRY);
  };

  /**
   * Overrides the `decodedQuery` `spot` value with value from GAC if the
   * region
   * id has not been changed.
   *
   * Currently the spot is decoded from the `geoMappings` and it's a 1:1
   * relationship between pretty URL and a label. However, GAC has synonyms,
   * e.g.
   * `1. Bezirk, Innere Stadt` is equivalent to `Wien - 01. Bezirk, Innere
   * Stadt`. Moreover GAC labels are also volatile and they can change in time.
   */
  overrideDecodedSpotWithGACLabelIfApplies = (
    location: any,
    decodedQuery: any,
  ) => {
    if (isEqual(HistoryActionEnum.POP, location.action)) {
      return decodedQuery;
    }
    const previousQuery =
      get(location, `state.${PREVIOUS_DECODED_QUERY}`) || {};
    const isTheSameRegionSelected = isEqualOnPaths(
      decodedQuery,
      previousQuery,
      [SearchParamsEnum.region as never],
    );
    if (!isTheSameRegionSelected) {
      return decodedQuery;
    }

    return {
      ...decodedQuery,
      [SearchParamsEnum.spot]: get(previousQuery, SearchParamsEnum.spot),
    };
  };

  /**
   * Gets the entity names from a given `query`.
   *
   * If it's a zip code based search, the name will be
   * resolved at this point, therefore no call in the API
   * as there's no special rule of how it's built.
   *
   * If the spot is the default spot or no region is specified,
   * or if GAC rejects a fallback hierarchy entity is returned.
   */
  getHierarchyEntityNames = (query: any) => {
    const isZipCodeBased =
      isNotBlank(query[SearchParamsEnum.zipCode]) &&
      isNotBlank(query[SearchParamsEnum.spot]);
    if (isZipCodeBased) {
      return bluebird.resolve([
        `${DEFAULT_PLZ_PREFIX} ${query[SearchParamsEnum.spot]}`,
      ]);
    }
    const isTheDefaultSpotSelected =
      isEqual(DEFAULT_SPOT, query[SearchParamsEnum.spot]) ||
      isNilOrBlank(query[SearchParamsEnum.region]);

    if (isTheDefaultSpotSelected) {
      return bluebird.resolve(FALLBACK_HIERARCHY_ENTITY_NAMES);
    }
    return bluebird
      .try(() =>
        this.localGeoApiClient.fetchGeoHierarchy(
          query[SearchParamsEnum.region],
        ),
      )
      .then(({ geoHierarchy }: any) => geoHierarchy)
      .catch(() => FALLBACK_HIERARCHY_ENTITY_NAMES);
  };
}

export default PrettyUrlsResultList;
